import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import type { CustomerRecord, RecordConfig, RecordFieldDataType } from './recordTypes';

// ─── Config (per-tenant record type + fields) ──────────────────────────────────

export function useRecordConfig(includeInactive = false) {
  return useQuery({
    queryKey: ['record-config', { includeInactive }],
    queryFn: async () => {
      const res = await apiClient.get<RecordConfig>('/api/v1/customer-records/config', {
        params: { includeInactive }
      });
      return res.data;
    },
    staleTime: 60_000
  });
}

export function useUpdateRecordLabels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { singularLabel: string; pluralLabel: string }) => {
      await apiClient.put('/api/v1/customer-records/config', body);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['record-config'] })
  });
}

export function useAddRecordField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { label: string; dataType: RecordFieldDataType; isRequired: boolean }) => {
      await apiClient.post('/api/v1/customer-records/config/fields', body);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['record-config'] })
  });
}

export function useUpdateRecordField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string;
      label?: string;
      dataType?: RecordFieldDataType;
      isRequired?: boolean;
      sortOrder?: number;
      isActive?: boolean;
    }) => {
      await apiClient.put(`/api/v1/customer-records/config/fields/${id}`, body);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['record-config'] })
  });
}

// ─── Records (a customer's configurable sub-records) ────────────────────────────

const invalidateRecords = (qc: ReturnType<typeof useQueryClient>, customerId: string) => {
  qc.invalidateQueries({ queryKey: ['customer-records', customerId] });
  qc.invalidateQueries({ queryKey: ['customer', customerId] });
  qc.invalidateQueries({ queryKey: ['customers'] }); // list count
};

export function useCustomerRecords(customerId: string | undefined) {
  return useQuery({
    queryKey: ['customer-records', customerId],
    queryFn: async () => {
      const res = await apiClient.get<CustomerRecord[]>(`/api/v1/customers/${customerId}/records`);
      return res.data;
    },
    enabled: !!customerId,
    staleTime: 15_000
  });
}

export function useCreateRecord(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, string>) => {
      const res = await apiClient.post<CustomerRecord>(`/api/v1/customers/${customerId}/records`, { data });
      return res.data;
    },
    onSuccess: () => invalidateRecords(qc, customerId)
  });
}

export function useUpdateRecord(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ recordId, data }: { recordId: string; data: Record<string, string> }) => {
      await apiClient.put(`/api/v1/customers/${customerId}/records/${recordId}`, { data });
    },
    onSuccess: () => invalidateRecords(qc, customerId)
  });
}

export function useDeleteRecord(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (recordId: string) => {
      await apiClient.delete(`/api/v1/customers/${customerId}/records/${recordId}`);
    },
    onSuccess: () => invalidateRecords(qc, customerId)
  });
}
